import React, { useState, useEffect, useRef } from 'react';
import { Database, Download, Upload, History, AlertTriangle, ShieldAlert, Check, Loader2, RefreshCw, FileArchive, FileJson, HardDrive, ShieldCheck, Clock, Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { backupsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(', ');
  return fallback;
};

const formatBytes = (b) => {
  if (!b) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(i ? 1 : 0)} ${sizes[i]}`;
};

const tipoBadge = (tipo) => {
  const map = {
    automatico: { label: 'Automático', cls: 'bg-blue-100 text-blue-700' },
    manual: { label: 'Manual', cls: 'bg-violet-100 text-violet-700' },
  };
  const m = map[tipo] || { label: tipo || '—', cls: 'bg-slate-100 text-slate-700' };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${m.cls}`}>{m.label}</span>;
};
import { Label } from '../../components/ui/label';
import { LoadingState } from '../../components/common/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AdminBackups = () => {
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);

  // Export
  const [selectedModule, setSelectedModule] = useState('master');
  const [selectedFormat, setSelectedFormat] = useState('json');
  const [exporting, setExporting] = useState(false);

  // Import / Restore
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('migracion');
  const [analyzing, setAnalyzing] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [confirmingRestore, setConfirmingRestore] = useState(false);
  const fileInputRef = useRef(null);

  // History
  const [history, setHistory] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [restoringSnap, setRestoringSnap] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  // Backups completos (object storage)
  const [fullBackups, setFullBackups] = useState([]);
  const [fullTotalBytes, setFullTotalBytes] = useState(0);
  const [loadingFull, setLoadingFull] = useState(false);
  const [creatingFull, setCreatingFull] = useState(false);
  const [restoringFull, setRestoringFull] = useState(null);
  const [restoreFullText, setRestoreFullText] = useState('');

  const loadModules = async () => {
    setLoadingModules(true);
    try {
      const r = await backupsApi.modules();
      setModules(r.data.modules || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando módulos');
    } finally {
      setLoadingModules(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await backupsApi.history();
      setHistory(r.data.history || []);
      setSnapshots(r.data.snapshots || []);
    } catch (e) {
      toast.error('Error cargando historial');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadModules();
    loadHistory();
    loadFullBackups();
  }, []);

  // ====== BACKUPS COMPLETOS ======
  const loadFullBackups = async () => {
    setLoadingFull(true);
    try {
      const r = await backupsApi.fullList();
      setFullBackups(r.data.backups || []);
      setFullTotalBytes(r.data.total_bytes || 0);
    } catch (e) {
      // silencioso al cargar
    } finally {
      setLoadingFull(false);
    }
  };

  const handleCreateFull = async () => {
    setCreatingFull(true);
    try {
      await backupsApi.fullCreate();
      toast.success('Backup completo creado y guardado en object storage');
      loadFullBackups();
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e, 'Error al crear el backup completo'));
    } finally {
      setCreatingFull(false);
    }
  };

  const handleDownloadFull = async (b) => {
    try {
      const r = await backupsApi.fullDownload(b.archive_id);
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = b.filename || `${b.archive_id}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Descarga iniciada');
    } catch (e) {
      toast.error(errMsg(e, 'Error al descargar'));
    }
  };

  const handleRestoreFull = async () => {
    if (!restoringFull) return;
    try {
      const r = await backupsApi.fullRestore(restoringFull.archive_id);
      toast.success(`Backup restaurado (${r.data.collections} colecciones, ${r.data.files_restored} archivos)`);
      loadModules();
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e, 'Error al restaurar'));
    } finally {
      setRestoringFull(null);
      setRestoreFullText('');
    }
  };

  // ====== EXPORT ======
  const currentModule = modules.find((m) => m.key === selectedModule);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await backupsApi.export(selectedModule, selectedFormat);
      const blob = new Blob([r.data], { type: selectedFormat === 'zip' ? 'application/zip' : 'application/json' });
      // Sacar filename del content-disposition
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="(.+)"/);
      const filename = match ? match[1] : `backup_${selectedModule}.${selectedFormat}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup descargado');
      loadHistory();
    } catch (e) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // ====== DRY-RUN ======
  const handleDryRun = async () => {
    if (!importFile) {
      toast.error('Seleccioná un archivo primero');
      return;
    }
    setAnalyzing(true);
    setDryRunResult(null);
    try {
      const r = await backupsApi.dryRun(importFile, importMode);
      setDryRunResult(r.data);
      toast.success('Análisis completado');
    } catch (e) {
      toast.error(errMsg(e, 'Error analizando archivo'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleImport = async () => {
    if (!dryRunResult?.dry_run_id) return;
    setImporting(true);
    try {
      const r = await backupsApi.import(
        dryRunResult.dry_run_id,
        importMode === 'restauracion_exacta'
      );
      toast.success(`Importación completa. Snapshot: ${r.data.snapshot_id}`);
      setDryRunResult(null);
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e, 'Error en el import'));
    } finally {
      setImporting(false);
      setConfirmingRestore(false);
    }
  };

  const handleRestoreSnapshot = async () => {
    if (!restoringSnap) return;
    try {
      await backupsApi.restoreSnapshot(restoringSnap.snapshot_id);
      toast.success('Snapshot restaurado');
      loadModules();
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e, 'Error al restaurar'));
    } finally {
      setRestoringSnap(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-backups-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Database className="w-6 h-6" /> Backups
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Exportar e importar backups por módulo. Solo accesible para administradores.
        </p>
      </div>

      <Tabs defaultValue="export" className="w-full">
        <TabsList>
          <TabsTrigger value="export" data-testid="tab-export">
            <Download className="w-4 h-4 mr-2" /> Exportar
          </TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">
            <Upload className="w-4 h-4 mr-2" /> Importar / Restaurar
          </TabsTrigger>
          <TabsTrigger value="full" data-testid="tab-full">
            <Cloud className="w-4 h-4 mr-2" /> Backups completos
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <ShieldCheck className="w-4 h-4 mr-2" /> Puntos de restauración
          </TabsTrigger>
        </TabsList>

        {/* ============ EXPORT ============ */}
        <TabsContent value="export" className="mt-6">
          {loadingModules ? <LoadingState message="Cargando módulos..." /> : (
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
              <div>
                <Label>Módulo a exportar</Label>
                <Select value={selectedModule} onValueChange={setSelectedModule}>
                  <SelectTrigger data-testid="export-module-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {modules.map((m) => (
                      <SelectItem key={m.key} value={m.key}>
                        {m.label} ({m.total_docs} docs)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentModule && (
                  <p className="text-xs text-slate-500 mt-1">{currentModule.description}</p>
                )}
              </div>

              <div>
                <Label>Formato</Label>
                <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                  <SelectTrigger data-testid="export-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="json">
                      <span className="flex items-center gap-2"><FileJson className="w-4 h-4" /> JSON (solo datos)</span>
                    </SelectItem>
                    {currentModule?.includes_files && (
                      <SelectItem value="zip">
                        <span className="flex items-center gap-2"><FileArchive className="w-4 h-4" /> ZIP (datos + archivos físicos)</span>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {!currentModule?.includes_files && (
                  <p className="text-xs text-slate-500 mt-1">Este módulo no incluye archivos físicos.</p>
                )}
              </div>

              {currentModule?.includes_password_hash && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                  <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-medium">Este backup contiene credenciales hasheadas</p>
                    <p className="text-xs">Guardalo en un lugar seguro. Tratar como secreto.</p>
                  </div>
                </div>
              )}

              <Button onClick={handleExport} disabled={exporting} data-testid="export-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {exporting ? 'Exportando...' : 'Descargar backup'}
              </Button>

              {currentModule && (
                <div className="text-xs text-slate-500 border-t pt-4">
                  <p className="font-medium mb-1">Colecciones incluidas:</p>
                  <ul className="space-y-0.5">
                    {Object.entries(currentModule.counts).map(([col, n]) => (
                      <li key={col} className="font-mono">
                        {col}: <span className="text-slate-700">{n}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ============ IMPORT / RESTORE ============ */}
        <TabsContent value="import" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium">Flujo seguro</p>
                <p className="text-xs">1) Seleccioná archivo → 2) Analizar (dry-run) → 3) Revisar resultado → 4) Confirmar importación. Antes de escribir se toma un snapshot automático.</p>
              </div>
            </div>

            <div>
              <Label>Archivo de backup (JSON o ZIP)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.zip"
                onChange={(e) => { setImportFile(e.target.files?.[0]); setDryRunResult(null); }}
                className="block mt-1 w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                data-testid="import-file-input"
              />
            </div>

            <div>
              <Label>Modo</Label>
              <Select value={importMode} onValueChange={(v) => { setImportMode(v); setDryRunResult(null); }}>
                <SelectTrigger data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="migracion">Migración (crear/actualizar, no borra)</SelectItem>
                  <SelectItem value="restauracion_exacta">Restauración Exacta (puede borrar extras)</SelectItem>
                </SelectContent>
              </Select>
              {importMode === 'restauracion_exacta' && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Restauración exacta puede BORRAR datos que estén en destino y no estén en el archivo.
                </p>
              )}
            </div>

            <Button onClick={handleDryRun} disabled={!importFile || analyzing} data-testid="dryrun-btn">
              {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {analyzing ? 'Analizando...' : 'Analizar (dry-run)'}
            </Button>

            {dryRunResult && (
              <div className="border-t pt-4 mt-4 space-y-4" data-testid="dryrun-result">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Análisis OK — dry_run_id: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{dryRunResult.dry_run_id}</code></span>
                </div>

                {dryRunResult.preview?.legacy_converted && (
                  <div className="text-xs p-2 bg-amber-50 border border-amber-200 rounded">
                    ⚠️ Backup en formato legacy v1 detectado y convertido automáticamente a v2.
                  </div>
                )}

                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 bg-green-50 border border-green-200 rounded text-center">
                    <p className="text-2xl font-bold text-green-700">{dryRunResult.preview?.totales?.a_crear || 0}</p>
                    <p className="text-xs text-green-700">A crear</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded text-center">
                    <p className="text-2xl font-bold text-blue-700">{dryRunResult.preview?.totales?.a_actualizar || 0}</p>
                    <p className="text-xs text-blue-700">A actualizar</p>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded text-center">
                    <p className="text-2xl font-bold text-slate-700">{dryRunResult.preview?.totales?.a_saltar || 0}</p>
                    <p className="text-xs text-slate-700">A saltar</p>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-200 rounded text-center">
                    <p className="text-2xl font-bold text-red-700">{dryRunResult.preview?.totales?.a_borrar || 0}</p>
                    <p className="text-xs text-red-700">A borrar</p>
                  </div>
                </div>

                {dryRunResult.preview?.warnings?.length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs">
                    <p className="font-medium">Advertencias:</p>
                    <ul className="list-disc pl-4">{dryRunResult.preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                  </div>
                )}

                <details className="text-sm">
                  <summary className="cursor-pointer text-slate-700 font-medium">Detalle por colección</summary>
                  <table className="w-full mt-2 text-xs">
                    <thead><tr className="text-left text-slate-500">
                      <th className="py-1">Colección</th>
                      <th>Crear</th><th>Actualizar</th><th>Saltar</th><th>Borrar</th><th>Errores</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(dryRunResult.preview?.by_collection || {}).map(([col, data]) => (
                        <tr key={col} className="border-t">
                          <td className="py-1 font-mono">{col}</td>
                          <td>{data.a_crear}</td><td>{data.a_actualizar}</td>
                          <td>{data.a_saltar}</td><td>{data.a_borrar}</td>
                          <td className={data.errores?.length ? 'text-red-600' : ''}>{data.errores?.length || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>

                {importMode === 'restauracion_exacta' ? (
                  <Button
                    onClick={() => setConfirmingRestore(true)}
                    disabled={importing}
                    variant="destructive"
                    data-testid="confirm-restore-btn"
                  >
                    Confirmar restauración exacta
                  </Button>
                ) : (
                  <Button onClick={handleImport} disabled={importing} data-testid="confirm-import-btn">
                    {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    {importing ? 'Importando...' : 'Confirmar importación'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ============ BACKUPS COMPLETOS (OBJECT STORAGE) ============ */}
        <TabsContent value="full" className="mt-6" data-testid="full-backups-tab">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-2">
                <Cloud className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-slate-900">Backups completos</h3>
                  <p className="text-xs text-slate-500">
                    Incluyen toda la base de datos + archivos físicos. Se generan automáticamente cada 15 días
                    y se guardan en object storage (persistente). Se conservan los últimos 6 automáticos; los manuales no se borran.
                  </p>
                </div>
              </div>
              <Button onClick={handleCreateFull} disabled={creatingFull} data-testid="create-full-btn">
                {creatingFull ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <HardDrive className="w-4 h-4 mr-2" />}
                {creatingFull ? 'Generando...' : 'Crear backup ahora'}
              </Button>
            </div>

            <div className="flex items-center gap-4 text-sm border-y py-2">
              <span className="text-slate-500">Espacio total ocupado:</span>
              <span className="font-semibold text-slate-900" data-testid="full-total-size">{formatBytes(fullTotalBytes)}</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-500">{fullBackups.length} backup(s)</span>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={loadFullBackups} disabled={loadingFull}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingFull ? 'animate-spin' : ''}`} /> Refrescar
              </Button>
            </div>

            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2">Fecha</th><th>Tipo</th><th>Tamaño</th><th>Estado</th><th>Usuario/Sistema</th><th className="text-right">Acciones</th>
              </tr></thead>
              <tbody>
                {fullBackups.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Aún no hay backups completos. Creá uno con "Crear backup ahora".</td></tr>}
                {fullBackups.map((b) => (
                  <tr key={b.archive_id} className="border-b border-slate-100" data-testid={`full-row-${b.archive_id}`}>
                    <td className="py-2 text-xs">{new Date(b.created_at).toLocaleString('es-AR')}</td>
                    <td>{tipoBadge(b.tipo)}</td>
                    <td className="text-xs">{b.estado === 'ok' ? formatBytes(b.size_bytes) : '—'}</td>
                    <td>
                      {b.estado === 'ok'
                        ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><Check className="w-3.5 h-3.5" /> OK</span>
                        : b.estado === 'error'
                          ? <span className="inline-flex items-center gap-1 text-xs text-red-600" title={b.error}><AlertTriangle className="w-3.5 h-3.5" /> Error</span>
                          : <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Clock className="w-3.5 h-3.5" /> {b.estado}</span>}
                    </td>
                    <td className="text-xs">{b.actor_email || (b.tipo === 'automatico' ? 'sistema' : '—')}</td>
                    <td className="text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-2" disabled={b.estado !== 'ok'}
                        onClick={() => handleDownloadFull(b)} data-testid={`full-download-${b.archive_id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Descargar
                      </Button>
                      <Button size="sm" variant="destructive" disabled={b.estado !== 'ok'}
                        onClick={() => { setRestoringFull(b); setRestoreFullText(''); }} data-testid={`full-restore-${b.archive_id}`}>
                        Restaurar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ============ HISTORY ============ */}
        <TabsContent value="history" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="puntos-restauracion">
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-green-600" /> Puntos de restauración
              </h3>
              <Button size="sm" variant="outline" onClick={loadHistory} disabled={loadingHistory}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingHistory ? 'animate-spin' : ''}`} /> Refrescar
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Se generan automáticamente antes de importar o restaurar datos. Sirven para volver atrás si algo sale mal.
            </p>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2">Punto</th><th>Razón</th><th>Usuario</th><th>Fecha</th><th></th>
              </tr></thead>
              <tbody>
                {snapshots.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-slate-400">Sin puntos de restauración</td></tr>}
                {snapshots.map((s) => (
                  <tr key={s.snapshot_id} className="border-b border-slate-100">
                    <td className="py-1.5 font-mono text-xs">{s.snapshot_id}</td>
                    <td className="text-xs">{s.reason}</td>
                    <td className="text-xs">{s.actor_email}</td>
                    <td className="text-xs">{new Date(s.created_at).toLocaleString('es-AR')}</td>
                    <td className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setRestoringSnap(s)} data-testid={`restore-${s.snapshot_id}`}>
                        Restaurar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Auditoría técnica (oculta por defecto) */}
          <div className="mt-4">
            <Button size="sm" variant="ghost" className="text-slate-500"
              onClick={() => setShowAudit((v) => !v)} data-testid="toggle-auditoria-btn">
              <History className="w-3.5 h-3.5 mr-1" /> {showAudit ? 'Ocultar auditoría técnica' : 'Ver auditoría técnica'}
            </Button>
          </div>

          {showAudit && (
            <div className="bg-white border border-slate-200 rounded-lg p-4 mt-2" data-testid="auditoria-tecnica">
              <h3 className="font-semibold text-slate-900 mb-3 text-sm">Actividad reciente (auditoría técnica)</h3>
              {loadingHistory ? <LoadingState /> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-slate-500 border-b">
                    <th className="py-2">Acción</th><th>Usuario</th><th>Fecha</th><th>Detalle</th>
                  </tr></thead>
                  <tbody>
                    {history.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400">Sin actividad</td></tr>}
                    {history.map((h) => (
                      <tr key={h.history_id} className="border-b border-slate-100">
                        <td className="py-1.5 capitalize">{h.accion}</td>
                        <td className="text-xs">{h.actor_email}</td>
                        <td className="text-xs">{new Date(h.timestamp).toLocaleString('es-AR')}</td>
                        <td className="text-xs text-slate-500">
                          {h.detalle?.archive_id && <span>archivo: {h.detalle.archive_id}</span>}
                          {h.detalle?.dry_run_id && <span>dry_run: {h.detalle.dry_run_id}</span>}
                          {h.detalle?.snapshot_id && <span> · snap: {h.detalle.snapshot_id}</span>}
                          {h.detalle?.size_bytes && <span> · {formatBytes(h.detalle.size_bytes)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Confirm restauracion exacta */}
      <AlertDialog open={confirmingRestore} onOpenChange={setConfirmingRestore}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Confirmar restauración exacta
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esto va a <strong>BORRAR {dryRunResult?.preview?.totales?.a_borrar || 0} documentos</strong> que están en destino pero no en el archivo.
              Se va a tomar un snapshot automático antes. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleImport} className="bg-red-600 hover:bg-red-700">
              Sí, restaurar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm restore snapshot */}
      <AlertDialog open={!!restoringSnap} onOpenChange={(o) => !o && setRestoringSnap(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar snapshot</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a restaurar el snapshot <code>{restoringSnap?.snapshot_id}</code> (creado {restoringSnap && new Date(restoringSnap.created_at).toLocaleString('es-AR')}).
              <br /><br />
              Esto reemplaza el contenido actual de las colecciones afectadas. Antes de hacerlo, se toma OTRO snapshot del estado actual para poder volver atrás.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreSnapshot}>Restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm restore FULL backup (confirmación fuerte: escribir RESTAURAR) */}
      <AlertDialog open={!!restoringFull} onOpenChange={(o) => { if (!o) { setRestoringFull(null); setRestoreFullText(''); } }}>
        <AlertDialogContent data-testid="full-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Restaurar backup completo
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vas a restaurar <strong>toda la base de datos y los archivos físicos</strong> al estado del backup
              del {restoringFull && new Date(restoringFull.created_at).toLocaleString('es-AR')}.
              Esto <strong>reemplaza el contenido actual</strong>. Antes se toma un punto de restauración del estado actual.
              <br /><br />
              Para confirmar, escribí <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">RESTAURAR</code>:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={restoreFullText}
            onChange={(e) => setRestoreFullText(e.target.value)}
            placeholder="RESTAURAR"
            data-testid="full-restore-confirm-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreFull}
              disabled={restoreFullText.trim().toUpperCase() !== 'RESTAURAR'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="full-restore-confirm-btn"
            >
              Sí, restaurar todo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBackups;
